import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import DeepLLogo from './icons/DeepLLogo';
import { api } from '../services/api';
import './DeepLComparison.css';

// Visible "second opinion" affordance shown next to a finished translation.
// Lets translators verify ChatsControl's output against DeepL inline without
// leaving the chat view. Lazy: hits DeepL only when clicked, then caches
// server-side on the document so re-opens are instant.

const SEEN_FLAG = 'cc_seen_deepl';

function DeepLComparison({ documentId, freshAttention = false }) {
  const { t } = useTranslation('components');
  const [status, setStatus] = useState('idle'); // idle | loading | available | unavailable
  const [lines, setLines] = useState(null);
  const [reason, setReason] = useState(null);
  const [open, setOpen] = useState(false);
  const [pulsing, setPulsing] = useState(freshAttention);
  // "NEW" pill: shown once per browser, hides forever after the first click.
  // Lets translators discover the second-opinion feature without us having to
  // re-pulse every translation.
  const [showNewBadge, setShowNewBadge] = useState(() => {
    try { return !localStorage.getItem(SEEN_FLAG); } catch { return false; }
  });

  // One-time attention-grabbing pulse the first time this button mounts after
  // a fresh translation completes. Stops as soon as the user interacts with it.
  useEffect(() => {
    if (!freshAttention) return undefined;
    setPulsing(true);
    const timer = setTimeout(() => setPulsing(false), 5400); // 3 pulses × 1.8s
    return () => clearTimeout(timer);
  }, [freshAttention]);

  const handleToggle = useCallback(async () => {
    setPulsing(false);
    if (showNewBadge) {
      try { localStorage.setItem(SEEN_FLAG, '1'); } catch { /* private mode - ok */ }
      setShowNewBadge(false);
    }

    if (status === 'available' || status === 'unavailable') {
      setOpen((o) => !o);
      return;
    }
    if (!documentId) return;

    setStatus('loading');
    setOpen(true);
    try {
      const data = await api.getComparison(documentId, 'deepl');
      if (data.available) {
        setLines(data.translated_lines || []);
        setStatus('available');
      } else {
        setReason(data.reason || 'api_error');
        setStatus('unavailable');
      }
    } catch (e) {
      console.error('DeepL comparison fetch failed:', e);
      setReason('api_error');
      setStatus('unavailable');
    }
  }, [status, documentId]);

  const buttonLabel = open
    ? t('diff.hideDeepl', 'Hide DeepL')
    : status === 'available' && !open
    ? t('diff.viewDeepl', 'View DeepL translation')
    : t('diff.compareWithDeepl', 'Compare with DeepL');

  return (
    <div className="deepl-comparison">
      <button
        className={`deepl-comparison-toggle ${open ? 'active' : ''} ${pulsing ? 'pulsing' : ''}`}
        onClick={handleToggle}
        disabled={status === 'loading'}
        title={buttonLabel}
      >
        {status === 'loading' ? (
          <Loader2 size={14} className="deepl-spinner" />
        ) : (
          <DeepLLogo size={14} />
        )}
        <span className="deepl-comparison-label">{buttonLabel}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {showNewBadge && <span className="feature-new-badge">NEW</span>}
      </button>

      {open && (
        <div className="deepl-comparison-panel">
          {status === 'loading' && (
            <div className="deepl-comparison-state">
              <Loader2 size={16} className="deepl-spinner" />
              <span>{t('diff.deeplLoading', 'Fetching DeepL translation...')}</span>
            </div>
          )}

          {status === 'unavailable' && (
            <div className="deepl-comparison-state muted">
              {reason === 'key_missing' && t('diff.deeplKeyMissing', 'DeepL comparison is not configured yet')}
              {reason === 'unsupported_language' && t('diff.deeplUnsupportedLang', "DeepL doesn't support this language pair")}
              {reason === 'api_error' && t('diff.deeplApiError', 'DeepL temporarily unavailable, try again later')}
            </div>
          )}

          {status === 'available' && (
            <div className="deepl-comparison-content">
              <div className="deepl-comparison-header">
                <DeepLLogo size={14} />
                <span>{t('diff.deeplPanelTitle', 'DeepL translation (for comparison)')}</span>
              </div>
              <div className="deepl-comparison-lines">
                {lines && lines.length > 0 ? lines.map((line, i) => (
                  <div
                    key={i}
                    className="deepl-comparison-line"
                    style={{ animationDelay: `${Math.min(i, 30) * 25}ms` }}
                  >
                    <span className="deepl-line-number">{i + 1}</span>
                    <span className="deepl-line-text">{line || ' '}</span>
                  </div>
                )) : (
                  <div className="deepl-comparison-state muted">
                    {t('diff.deeplEmpty', 'DeepL returned no translation')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DeepLComparison;
